/**
 * Normalizes an expert-supplied employer link into a bare company domain.
 *
 * Experts paste whatever is in their address bar, so the input is anything
 * from `terac.com` to `https://www.terac.com/careers?ref=x`. Matching keys
 * on the host alone, and storing the normalized form keeps that comparison a
 * plain equality check rather than a per-read parse.
 */

/**
 * Hosts that describe a company without being one. A profile page on any of
 * these identifies the directory, so accepting it would key every unrelated
 * employer to the same domain.
 */
const DIRECTORY_HOSTS = new Set([
  "angel.co",
  "bloomberg.com",
  "crunchbase.com",
  "facebook.com",
  "glassdoor.com",
  "indeed.com",
  "instagram.com",
  "linkedin.com",
  "pitchbook.com",
  "twitter.com",
  "wellfound.com",
  "x.com",
  "youtube.com",
  "zoominfo.com",
]);

export function isCompanyDirectoryHost(host: string): boolean {
  return (
    DIRECTORY_HOSTS.has(host) ||
    [...DIRECTORY_HOSTS].some((directory) => host.endsWith(`.${directory}`))
  );
}

/**
 * Returns the bare host, or null when the input is not usable as a company
 * domain. Null covers blank input, unparseable text, bare hostnames with no
 * dot, and directory hosts.
 */
export function normalizeCompanyDomain(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return null;
  }

  // `new URL` needs a scheme, and a bare domain is the common paste.
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  let host: string;
  try {
    host = new URL(candidate).hostname.toLowerCase();
  } catch {
    return null;
  }

  host = host.replace(/^www\./, "").replace(/\.$/, "");
  // A single label is a hostname, not a company domain, and "localhost" or a
  // typo like "microsoft" would otherwise be stored as if it resolved.
  if (!host.includes(".")) {
    return null;
  }
  if (isCompanyDirectoryHost(host)) {
    return null;
  }
  return host;
}

/**
 * Same company when either domain is the other or sits under it, so a
 * regional site or a mail subdomain still matches. A different registrable
 * name, regional TLD included (`acme.co.uk` against `acme.com`), does not.
 */
export function companyDomainMatchesEmail(
  companyDomain: string,
  email: string,
) {
  const emailDomain = normalizeCompanyDomain(
    email.slice(email.lastIndexOf("@") + 1),
  );
  if (!emailDomain) {
    return false;
  }
  return (
    emailDomain === companyDomain ||
    emailDomain.endsWith(`.${companyDomain}`) ||
    companyDomain.endsWith(`.${emailDomain}`)
  );
}
